import React from "react";
import { FaBox, FaShoppingCart, FaCheck } from 'react-icons/fa';

const ItemsSection = ({ checkOutData, subTotal, isPaymentMethodSelected }) => {
  // Helper function to get product image URL from either JSON array or direct URL
  const getProductImageUrl = (productImage) => {
    if (!productImage) return '/api/placeholder/300/300';
    try {
      // Try parsing as JSON first (legacy format)
      const productImages = JSON.parse(productImage);
      return Array.isArray(productImages) ? productImages[0] : productImage;
    } catch {
      // If parsing fails, it's already a direct URL (new format)
      return productImage;
    }
  };

  const itemCount = checkOutData?.length || 0;
  const isBuyNowOrder = checkOutData?.some(item => item.buyNow === true);

  return (
    <div className={`bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100 transition-all duration-300 ${
      !isPaymentMethodSelected ? "opacity-50 pointer-events-none" : "animate-fade-in"
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <span className="bg-orange-100 text-orange-600 rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold mr-3">
            4
          </span>
          Order Items & Shipping
        </h2>
        <div className="flex items-center space-x-2">
          {isBuyNowOrder && (
            <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
              Buy Now Order
            </span>
          )}
          <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </div>
        </div>
      </div>
      
      {checkOutData && checkOutData.length > 0 ? (
        <div className="space-y-6">
          {/* Items List */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="space-y-4">
              {checkOutData.map((item, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="flex items-start space-x-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0 relative">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-orange-300 transition-colors">
                        <img 
                          src={getProductImageUrl(item.productImage)} 
                          alt={item.productName}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/300/300';
                          }}
                        />
                      </div>
                      {/* Stock indicator */}
                      {item.inStock && (
                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                          <FaCheck className="text-xs" />
                        </div>
                      )}
                    </div>
                    
                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                        <div className="flex-1 mb-2 md:mb-0">
                          <h3 className="font-semibold text-gray-900 text-lg leading-tight group-hover:text-orange-600 transition-colors">
                            {item.productName}
                          </h3>
                          <div className="flex items-center space-x-3 mt-2">
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-sm font-medium">
                              Qty: {item.quantity}
                            </span>
                            <span className="text-sm text-gray-600">
                              KSH {item.price.toLocaleString('en-KE')} per item
                            </span>
                          </div>
                        </div>
                        
                        {/* Price */}
                        <div className="text-right">
                          <p className="text-xl font-bold text-orange-600">
                            KSH {(item.price * item.quantity).toLocaleString('en-KE')}
                          </p>
                          {item.quantity > 1 && (
                            <p className="text-sm text-gray-500">
                              {item.quantity} × KSH {item.price.toLocaleString('en-KE')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaBox className="mr-2 text-orange-600" />
              Order Summary
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center text-gray-600">
                <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):</span>
                <span className="font-semibold text-gray-800">
                  KSH {subTotal.toLocaleString('en-KE')}
                </span>
              </div>
              
              <div className="flex justify-between items-center text-gray-600">
                <span>Estimated delivery:</span>
                <span className="text-green-600 font-medium">3-5 business days</span>
              </div>
              
              <div className="pt-3 border-t border-gray-300">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-800">Items Total:</span>
                  <span className="text-xl font-bold text-orange-600">
                    KSH {subTotal.toLocaleString('en-KE')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Empty Cart State */
        <div className="text-center py-12">
          <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <FaShoppingCart className="text-3xl text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No items in your order</h3>
          <p className="text-gray-600">Your cart is empty. Add some items to continue.</p>
        </div>
      )}
    </div>
  );
};

export default ItemsSection;